import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxBorder, Color } from '@genial/design-tokens';
import { Service } from '@genial/services';
import { Box, Button, TextArea } from 'native-base';
import React, { useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputFocusEventData } from 'react-native';

import { PublicOfferApplicationProps, PublicOfferUserSelectionValues } from '../..';
import { useToastCreator } from '../../../../../../../../../../hooks/useToastCreator';
import { ReturnTypeOf_RealizarPedidoReserva } from '../../../../../../../../../../store/product/external/types';
import {
  PublicOfferAssetToInvestNormalized,
  PublicOfferDetailsModalityWithTimelineNormalized,
} from '../../../../../../../../../../store/product/types';
import { DetailsItemProps } from '../../../../components/LegacyApplicationSuccessDetails';
import { ProductCard } from '../../../../components/ProductCard';
import { screenName } from '../../../../constants';

import { getMaximumRate, getProductConfirmationInfos, getProductSuccessInfos } from './constants';

type Modality = PublicOfferDetailsModalityWithTimelineNormalized;

export interface PublicOfferConfirmationProps {
  onClose: () => void;
  offer: PublicOfferApplicationProps['offer'];
  selectedModality: Modality;
  userSelectionValues: PublicOfferUserSelectionValues;
  publicOfferAssetDetails?: PublicOfferAssetToInvestNormalized;
  accountId: number;
  onSuccess: (successInfos: DetailsItemProps[]) => void;
  isManager: boolean;
}

export const PublicOfferConfirmation: React.FC<PublicOfferConfirmationProps> = ({
  onClose,
  offer,
  onSuccess,
  isManager,
  accountId,
  selectedModality,
  userSelectionValues,
  publicOfferAssetDetails,
}) => {
  const { showToast } = useToastCreator('publicOfferApplicationInCustomer');
  const [isExecuting, setIsExecuting] = useState(false);
  const [selectedOption, setSelectedOption] = useState('Telefone');
  const [textAreaInput, setTextAreaInput] = useState('');
  const [customChannel, setCustomChannel] = useState('');
  const [authorizedRequest, setAuthorizedRequest] = useState(false);

  const shouldShowCustomChannel = selectedOption === 'Outros';

  const handleCustomChannelChange = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    setCustomChannel(e.nativeEvent.text);
  };

  const handlePressConfirm = () => {
    setIsExecuting(true);
    Service.External.Extranet.RendaVariavel.V1.rendaVariavelRealizarPedidoReserva({
      pedido: {
        numeroOferta: offer.offerId,
        idConta: accountId,

        precoMaximoTaxaSolicitada: getMaximumRate(offer, userSelectionValues),
        quantidade: userSelectionValues.amountItem,

        data: new Date(),
        codigoModalidade: selectedModality?.codigoModalidade,
        valorOferta: userSelectionValues.currentValue,
        solicitaPrecoAMercado:
          userSelectionValues.useMarketValueEquity ||
          userSelectionValues.useMarketValueRemuneration,

        ...(!isManager
          ? { tipoConfirmacaoOrdem: selectedOption, customChannel, observacao: textAreaInput }
          : {}),
      },
      screenName,
    })
      .then((data) => {
        const response = data as ReturnTypeOf_RealizarPedidoReserva;

        if (!response.sucesso) {
          return showToast(
            'error',
            response.exception?.[0].Message || 'Erro ao realizar a operação',
            response.retorno?.mensagem ||
              'Não foi possível realizar está operação, tente novamente mais tarde.'
          );
        }

        const successInfos = getProductSuccessInfos(
          offer,
          userSelectionValues,
          response,
          publicOfferAssetDetails
        );
        onSuccess(successInfos);
      })
      .finally(() => {
        setIsExecuting(false);
      });
  };

  useEffect(() => {
    if (shouldShowCustomChannel) {
      setCustomChannel('');
    }
  }, [shouldShowCustomChannel]);

  return (
    <VStack space="stack.xxxs">
      <ProductCard
        infos={getProductConfirmationInfos(offer, userSelectionValues, publicOfferAssetDetails)}
        productColor="product.ofertapublica"
        title={offer.offerName}
        BottomComponent={
          isManager ? undefined : (
            <VStack space="stack.xxxs">
              <VStack space={'stack.quarck'} w="full">
                <UI.Text color="neutral.black" variant="semibold">
                  Selecione o canal
                </UI.Text>
                <Stack bgColor="background" borderRadius={BoxBorder.thick}>
                  <UI.Select
                    placeholder="Selecione o canal"
                    options={[
                      { label: 'Via Telefone', value: 'Telefone' },
                      { label: 'Via E-mail', value: 'E-mail' },
                      { label: 'Outros', value: 'Outros' },
                    ]}
                    selectedValue={selectedOption}
                    onValueChange={setSelectedOption}
                  />
                </Stack>
              </VStack>

              {!!shouldShowCustomChannel && (
                <VStack space={'stack.quarck'} w="full">
                  <UI.Text color="neutral.black" variant="semibold">
                    Nome do canal
                  </UI.Text>
                  <UI.Input
                    onBlur={handleCustomChannelChange}
                    name="custom-channel"
                    autoComplete="off"
                    hasShadow={false}
                    variant="filled"
                    placeholder="Nome do canal"
                  />
                </VStack>
              )}

              <VStack space={'stack.quarck'} w="full">
                <UI.Text color="neutral.black" variant="semibold">
                  Observação
                </UI.Text>
                <Box height="80px">
                  <TextArea
                    flex={1}
                    borderRadius={BoxBorder.thick}
                    backgroundColor={Color.background}
                    borderColor="transparent"
                    _focus={{
                      backgroundColor: 'transparent',
                      borderColor: Color['brand.primary.principal'],
                      borderWidth: 1,
                    }}
                    _hover={{ borderColor: Color['brand.primary.principal'] }}
                    value={textAreaInput}
                    placeholder="Existe alguma informação sobre essa autorização"
                    autoCompleteType={'off'}
                    onChangeText={setTextAreaInput}
                  />
                </Box>
              </VStack>
            </VStack>
          )
        }
      />
      {!isManager && (
        <UI.CheckableNotice
          isSelected={authorizedRequest}
          onCheckboxChange={setAuthorizedRequest}
          textObjects={[
            {
              text: 'Atenção: ',
              variant: 'semibold',
            },
            {
              text: 'Declaro que a solicitação de aplicação foi autorizada pelo cliente, de forma prévia á realização desta operação, e que a mesma foi recebida por canal autorizado e gravado',
              variant: 'regular',
            },
          ]}
        />
      )}
      <HStack alignSelf="end" space="inline.xxxs" w="full" justifyContent="space-between">
        <UI.Button variant="secondary" onPress={onClose} w="45%">
          Cancelar
        </UI.Button>
        <Button
          variant="callToAction"
          onPress={handlePressConfirm}
          isDisabled={
            (!authorizedRequest || (shouldShowCustomChannel && !customChannel)) && !isManager
          }
          isLoadingText="Confirmando..."
          spinnerPlacement="end"
          isLoading={isExecuting}
          w="45%">
          Confirmar
        </Button>
      </HStack>
    </VStack>
  );
};
